import React, { useEffect, useMemo, useRef, useState } from "react";
import "katex/dist/katex.min.css"; // Keep for basic math styling
// Temml ships the MathML styling its output depends on. Import it here, beside the
// component that renders Temml, rather than relying on the stray global `math {}`
// rule in lesson.css - that rule only reached this page because webpack hoisted
// every stylesheet into one bundle. Vite code-splits CSS per chunk, so an import
// co-located with the consumer is the only bundler-independent way to guarantee it.
import "temml/dist/Temml-Local.css";
import { enhanceImage, enhanceDocumentLink } from "../utils/utlis";
import Modal from "@mui/material/Modal";
import { marked } from "marked";


// ADD THESE NEW IMPORTS
import temml from "temml";
import FilePreviewModal2 from "./FilePreview/FilePreviewModal2.tsx";

// ADD THIS: Temml configuration
const temmlOptions = {
  displayMode: false,
  throwOnError: false,
  strict: false,
  trust: true,
  colorIsTextColor: true,
  macros: {
    "\\RR": "\\mathbb{R}",
    "\\ZZ": "\\mathbb{Z}",
    "\\NN": "\\mathbb{N}",
    "\\QQ": "\\mathbb{Q}",
    "\\CC": "\\mathbb{C}",
  }
};

// ADD THIS: Expression caching for better performance
const temmlCache = new Map();

// Check if LaTeX contains matrix environments (for stretchy bracket fix)
const hasMatrixEnvironment = (latex) => {
  const matrixPattern = /\\begin\{(pmatrix|bmatrix|vmatrix|Vmatrix|matrix)\}|\\left\s*\([\s\S]*?\\begin\s*\{matrix\}/;
  return matrixPattern.test(latex);
};

// Fix stretchy brackets in MathML so matrix parentheses scale to matrix height
const fixStretchyBrackets = (mathml) => {
  return mathml
    .replace(
      /<mo([^>]*fence="true"[^>]*)>/g,
      (match, attrs) => {
        if (!attrs.includes('stretchy=')) {
          return `<mo${attrs} stretchy="true">`;
        }
        return match;
      }
    )
    .replace(
      /<mo([^>]*fence="true"[^>]*stretchy="true"[^>]*)>/g,
      (match, attrs) => {
        if (!attrs.includes('symmetric=')) {
          return `<mo${attrs} symmetric="true">`;
        }
        return match;
      }
    );
};


// ADD THIS: Temml rendering function with caching and matrix bracket fix
const renderLatexWithTemml = (latex, displayMode = false) => {
  const cacheKey = `temml_${latex}_${displayMode}`;
  if (temmlCache.has(cacheKey)) {
    return temmlCache.get(cacheKey);
  }

  try {
    let mathml = temml.renderToString(latex, {
      ...temmlOptions,
      displayMode
    });

    // Fix stretchy brackets so matrix parentheses scale to matrix height
    if (hasMatrixEnvironment(latex)) {
      mathml = fixStretchyBrackets(mathml);
    }

    const html = displayMode
      ? `<div class="temml-display">${mathml}</div>`
      : `<span class="temml-inline">${mathml}</span>`;

    temmlCache.set(cacheKey, html);
    return html;
  } catch (error) {
    // console.warn('Temml render error:', error, 'for expression:', latex);
    return `<code class="math-error" title="${error.message}">${latex}</code>`;
  }
};

// Lexical exports a <li><br></li> for every blank line the author left between
// bullets, and marked() passes that HTML straight through - so the rendered list
// came out with a bare marker on every other row. Drop list items that carry no
// content at all (no text, no media, no nested list), then renumber the explicit
// value="n" markers Lexical writes so the removals leave no gaps in an <ol>.
//
// Parsing with DOMParser rather than pattern-matching keeps nested lists intact;
// the result is the same DOM the browser would build for this string anyway, since
// the HTML ends up in innerHTML either way.
const removeEmptyListItems = (html) => {
  if (!html || !/<li/i.test(html)) return html;

  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    if (!doc || !doc.body) return html;

    doc.querySelectorAll("li").forEach((li) => {
      // An item holding media, a control or a nested list is never "empty",
      // even when it carries no text of its own.
      if (
        li.querySelector(
          "img, svg, math, video, audio, iframe, input, a, ul, ol, table"
        )
      ) {
        return;
      }
      if (li.textContent.replace(/\u00a0/g, " ").trim() === "") {
        li.remove();
      }
    });

    doc.querySelectorAll("ul, ol").forEach((list) => {
      let index = 1;
      Array.from(list.children).forEach((child) => {
        if (child.tagName !== "LI") return;
        if (child.hasAttribute("value")) child.setAttribute("value", String(index));
        index += 1;
      });
    });

    // Any parser that cannot round-trip this string must not blank the message out.
    return doc.body.innerHTML.trim() || html;
  } catch (error) {
    // A malformed string must never blank out the message - render it untouched.
    return html;
  }
};

const decodeHtmlEntities = (str) => {
  const textarea = document.createElement("textarea");
  textarea.innerHTML = str;
  return textarea.value;
};

// ADD THIS: Enhanced sanitization function
const sanitizeLatex = (latex) => {

  return latex
    .replace(/</g, ' \\lt ')   // Use \lt instead of < (valid LaTeX)
    .replace(/>/g, ' \\gt ');
};

const PreprocessAndRenderLatex = ({
  content,
  isBlockMath,
  wrapperStyle,
  latexInline = false,
}) => {
  // MODIFY THIS: Add preprocessing



  const expandStartEndLinks = (str) => {
    return str.replace(
      /\[start_name\]([\s\S]*?)\[end_name\][\s\S]*?\[start_link\]([\s\S]*?)\[end_link\]/g,
      (_, nameContent, linkContent) => {
        let url = linkContent.trim();
        let linkName = nameContent.trim();

        // 1️⃣ If anchor tag exists → extract href
        const anchorMatch = url.match(/href=["']([^"']+)["']/i);
        if (anchorMatch) {
          url = anchorMatch[1];
        }

        // 2️⃣ Extract valid file URL
        const fileMatch = url.match(
          /(https?:\/\/[^\s"'<>]+?\.(pptx|pdf|docx?|xlsx?|csv|txt|zip|jpg|png)(\?[^\s"'<>]*)?)/i
        );

        if (!fileMatch) return "";

        const cleanUrl = fileMatch[1];

        // 3️⃣ Remove extension from name
        linkName = linkName.replace(
          /\.(pptx|pdf|docx?|xlsx?|csv|txt|zip|jpg|png)$/i,
          ""
        );

        if (!linkName) linkName = "Document";

        return `<a class="download_link" href="${cleanUrl}">${linkName}</a>`;
      }
    );
  };



  // Normalize React-style className to class so document links get .download_link in DOM
  const normalizedContent =
    typeof content === "string"
      ? expandStartEndLinks(content).replace(/\bclassName="download_link"/gi, 'class="download_link"')
      : content;


  let preprocessedContent = normalizedContent;


  const tableRegex =
    /(\|.*\|(?:\r?\n|\r)\|[-:\s|]+\|(?:[\s\S]*?))(?=\n\s*\n|$)/g;



  preprocessedContent = preprocessedContent.replace(
    tableRegex,
    (tableMarkdown) => {
      // Step 1: Replace entire $$...$$ blocks with opaque tokens BEFORE marked()
      const mathStore = [];
      const protectedMarkdown = tableMarkdown.replace(
        /\$\$([\s\S]*?)\$\$/g,
        (_, latex) => {
          const idx = mathStore.length;
          mathStore.push(latex);
          return `XMATH${idx}XENDX`; // no special chars — marked won't touch this
        }
      );

      // Step 2: marked() now sees no LaTeX at all — table parses perfectly
      let tableHtml = marked(protectedMarkdown);

      // Step 3: Swap tokens back with rendered Temml
      tableHtml = tableHtml.replace(/XMATH(\d+)XENDX/g, (_, idx) => {
        const latex = mathStore[parseInt(idx, 10)];
        const decodedLatex = decodeHtmlEntities(latex);
        const cleanLatex = sanitizeLatex(decodedLatex);
        return renderLatexWithTemml(cleanLatex, false);
      });

      return tableHtml;
    }
  );


  const addTargetBlankToLinks = (html) =>
    html.replace(/<a\s+([^>]*href=['"][^'"]+['"][^>]*)>/gi, (match, p1) => {
      if (/target\s*=\s*['"]_blank['"]/i.test(match)) return match;
      return `<a ${p1} target="_blank" rel="noopener noreferrer">`;
    });

  // Markdown has to be parsed in ONE pass over the whole message. Splitting the
  // string on `$$` and running marked() on each slice tore block-level markdown
  // apart: for `1. Solve $$x^2 = 4$$ for x` the <ol> was closed before the math,
  // so the formula (and the text after it) dropped onto the next line as separate
  // blocks - leaving the list marker orphaned on a line of its own.
  //
  // Instead every `$$...$$` is swapped for an inert alphanumeric token, marked()
  // sees intact markdown, and the rendered Temml is put back afterwards - which
  // keeps the math inline inside its own <li>/<p>.
  const renderedHtml = useMemo(() => {
    if (typeof preprocessedContent !== "string") return "";

    const mathStore = [];
    const tokenized = preprocessedContent.replace(
      /\$\$([\s\S]*?)\$\$/g,
      (_, latex) => `XLATEX${mathStore.push(latex) - 1}XENDX` // no special chars - marked won't touch it
    );

    let html = marked(tokenized);

    // A $$...$$ written on its own source line becomes its own <p>, so a
    // sentence the author meant as one line -
    //   $$P(A|B)$$
    //
    //   = probability of A given B
    // - rendered as two. When a paragraph holds nothing but math and the paragraph
    // after it continues the same sentence (starts with an operator, punctuation or
    // a lowercase word), fold the two back into one. This runs while the math is
    // still an opaque token, so "paragraph holds nothing but math" is a plain
    // string test - no DOM walking needed.
    html = html.replace(
      /<p>((?:\s*XLATEX\d+XENDX)+)\s*<\/p>\s*<p>\s*(?=[=<>+\-−×÷·→,;:)\]}]|≈|≠|≤|≥|[a-z])/g,
      (_, mathTokens) => `<p>${mathTokens} `
    );

    // When latexInline: avoid block <p> so text + math stay on one line
    if (latexInline) {
      html = html.replace(/<p>/gi, "<span>").replace(/<\/p>/gi, "</span>");
    }

    // Runs while the math is still an opaque token, so a list item holding nothing
    // but a formula still reads as non-empty text and survives the cleanup.
  html = removeEmptyListItems(html);

    html = html.replace(/XLATEX(\d+)XENDX/g, (match, idx) => {
      const raw = mathStore[parseInt(idx, 10)];
      if (raw === undefined) return match;

      const latex =
        new DOMParser().parseFromString(sanitizeLatex(raw), "text/html").body
          .textContent || "";

      // Skip empty LaTeX expressions
      if (!latex.trim()) return "";

      return renderLatexWithTemml(latex, false);
    });

    return addTargetBlankToLinks(html);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preprocessedContent, latexInline]);

  // IMPORTANT: the `dangerouslySetInnerHTML` object must be memoized.
  // React re-writes a node's innerHTML whenever the dangerouslySetInnerHTML prop
  // changes identity - even when the HTML string is identical - so a fresh
  // `{ __html: ... }` literal per render silently wiped out every DOM enhancement
  // applied by the effects below (image wrappers, document link cards, matrix
  // bracket scaling) on any unrelated parent re-render.
  const contentNode = useMemo(() => ({ __html: renderedHtml }), [renderedHtml]);

  const containerRef = useRef(null);
  const graphImageRef = useRef(null);

  // Image popup state (keep as is)
  const [isImagePopupOpen, setIsImagePopupOpen] = useState(false);
  const [openFile, setOpenFile] = useState(false);
  const [popupImageSrc, setPopupImageSrc] = useState(null);

  const openImagePopup = (src) => {
    setPopupImageSrc(src);
    setIsImagePopupOpen(true);
  };

  const closeImagePopup = () => {
    setIsImagePopupOpen(false);
    setPopupImageSrc(null);
  };
  const openFilePopup = (file) => {
    setOpenFile({ ...file });
  };

  const closeFilePopup = () => {
    setOpenFile(null);
  };


  // Both enhancement effects below run after every commit rather than only when the
  // content string changes. The data-* flags make them idempotent, and running them
  // unconditionally means they re-apply themselves if the rendered HTML is ever
  // re-written for a reason the content string cannot see.
  useEffect(() => {
    if (!graphImageRef.current) return;

    const images = graphImageRef.current.querySelectorAll("img.graph-image, img");

    images.forEach((img) => {
      if (img.dataset.enhanced === "true") return;
      enhanceImage(img, openImagePopup);
      img.dataset.enhanced = "true";
    });
  });

  // Enhance document links (e.g. download_link, PDF links) into a styled "Document" box
  useEffect(() => {
    if (!graphImageRef.current) return;

    const docLinks = graphImageRef.current.querySelectorAll(
      'a.download_link, a[className="download_link"], a[href*=".pdf"], a[href*=".pptx"], a[href*=".docx"], a[href*=".xlsx"], a[href*=".csv"], a[href*=".txt"]'
    );

    docLinks.forEach((anchor) => {
      if (anchor.dataset.documentEnhanced === "true") return;
      enhanceDocumentLink(anchor, openFilePopup);
      anchor.dataset.documentEnhanced = "true";
    });
  });

  // Scale matrix fence brackets to match matrix height (browsers often ignore MathML stretchy="true")
  // Run at multiple delays so brackets fix during streaming (when layout may not be ready at 50ms)
  useEffect(() => {
    const container = graphImageRef.current;
    if (!container) return;

    const fixMatrixBrackets = () => {
      if (!container.isConnected) return;
      const mathElements = container.querySelectorAll("math");
      mathElements.forEach((mathEl) => {
        const tables = mathEl.querySelectorAll("mtable");
        tables.forEach((mtable) => {
          const mrow = mtable.parentElement;
          if (!mrow || mrow.tagName !== "mrow") return;

          const leftBracket = mrow.querySelector(':scope > mo[fence="true"][form="prefix"]');
          const rightBracket = mrow.querySelector(':scope > mo[fence="true"][form="postfix"]');
          if (!leftBracket || !rightBracket) return;
          if (leftBracket.dataset.matrixScaled === "true") return;

          const tableRect = mtable.getBoundingClientRect();
          const bracketRect = leftBracket.getBoundingClientRect();
          const tableHeight = tableRect.height;
          const bracketHeight = bracketRect.height;
          if (bracketHeight <= 0 || tableHeight <= 0) return;

          const scale = tableHeight / bracketHeight;
          const style = (el) => {
            el.style.height = `${tableHeight}px`;
            el.style.minWidth = `${bracketRect.width}px`;
            el.style.display = "inline-flex";
            el.style.alignItems = "center";
            el.style.justifyContent = "center";
            el.style.overflow = "visible";
            el.style.transform = `scaleY(${scale})`;
            el.style.transformOrigin = "center";
            el.dataset.matrixScaled = "true";
          };
          style(leftBracket);
          style(rightBracket);
        });
      });
    };

    const runAfterPaint = (fn) => {
      requestAnimationFrame(() => requestAnimationFrame(fn));
    };

    const t1 = setTimeout(() => runAfterPaint(fixMatrixBrackets), 0);
    const t2 = setTimeout(() => runAfterPaint(fixMatrixBrackets), 80);
    const t3 = setTimeout(fixMatrixBrackets, 200);
    const t4 = setTimeout(fixMatrixBrackets, 450);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, [renderedHtml]);


  // ADD THIS: CSS for Temml styling
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .temml-display {
        display: block;
        text-align: center;
        margin: 1em 0;
      }
      
      .temml-inline {
        display: inline;
        vertical-align: baseline;
      }
      
      .math-error {
        background-color: #ffebee;
        color: #c62828;
        padding: 2px 4px;
        border-radius: 3px;
        font-family: monospace;
        font-size: 0.9em;
      }

      math {
        font-size: inherit;
      }
      
      math[display="block"] {
        display: block;
        text-align: center;
        margin: 1em 0;
      }

      /* Force fence operators (matrix brackets) to stretch to content height */
      mo[fence="true"][stretchy="true"] {
        vertical-align: middle;
      }

      mtable {
        vertical-align: middle;
        margin: 0 0.2em;
      }

      mrow:has(> mtable) {
        display: inline-flex;
        align-items: center;
        vertical-align: middle;
      }

      mrow > mo[fence="true"][stretchy="true"] {
        display: inline-flex;
        align-items: center;
      }

      /* The app resets ul/ol to list-style:none with zero margin and padding globally
         (src/index.css), which strips the markers off markdown lists rendered here.
         Re-enable them ONLY inside this component's output - scoping every rule to
         .latex-markdown-content leaves every other list in the app untouched. */
      .latex-markdown-content ul,
      .latex-markdown-content ol {
        list-style-position: outside;
        padding-left: 1.5em;
        margin: 0.5em 0;
      }

      .latex-markdown-content ul { list-style-type: disc; }
      .latex-markdown-content ol { list-style-type: decimal; }
      .latex-markdown-content ul ul { list-style-type: circle; }
      .latex-markdown-content ul ul ul { list-style-type: square; }
      .latex-markdown-content ol ol { list-style-type: lower-alpha; }
      .latex-markdown-content ol ol ol { list-style-type: lower-roman; }

      .latex-markdown-content li {
        display: list-item;
        margin: 0.15em 0;
      }

      /* Loose lists wrap each item's text in <p>, which the global 20px bottom
         margin on p then pushes below the marker. Keep the first
         paragraph inline so the text - and any inline math in it - sits on the
         marker's line; later paragraphs of the same item still break normally. */
      .latex-markdown-content li > p {
        display: inline;
        margin: 0;
      }

      .latex-markdown-content li > p ~ p {
        display: block;
        margin-top: 0.35em;
      }

      /* Math inside a list item must never force a line break. */
      .latex-markdown-content li .temml-inline {
        display: inline;
      }

      .latex-markdown-content li math {
        vertical-align: baseline;
      }

      /* Temml-Local.css makes every <math> a flex container in Chromium
         (display: inline-flex + flex-wrap: wrap) so long formulas can line-break.
         That takes the math root out of MathML layout: an mfrac inside it then lays
         out like a block box and its fraction bar is painted across the full line -
         which is why 1/2 came out as a page-wide rule with a tiny 1 and 2 on it,
         pushing the rest of the sentence onto the next line.

         Put the root back into real MathML inline layout. Plain "inline" is the
         Firefox/Safari fallback; Chromium takes the two-value "inline math" that
         follows, while Firefox drops it as invalid and keeps "inline" - which is
         exactly what Temml sets for Firefox in its own @-moz-document block.

         Matrices are deliberately left on Temml flex path: fixMatrixBrackets
         above measures and scales their fences against that layout. */
      .latex-markdown-content math:not(.tml-display):not(:has(mtable)) {
        display: inline;
        display: inline math;
        max-width: 100%;
      }

      /* The 0.5ex of row padding exists only to separate wrapped flex lines. */
      .latex-markdown-content math:not(:has(mtable)) > mrow {
        padding: 0;
      }

      /* Browsers default <pre> to a monospace face around 13px; pin preformatted
         blocks to the same 1rem the surrounding prose uses so editor content does
         not render smaller than the text around it. */
      .latex-markdown-content pre,
      .latex-markdown-content pre code,
      .latex-markdown-content pre span {
        font-size: 1rem;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);




  const Container = latexInline ? "span" : "div";
  return (
    <>
      <Container
        ref={graphImageRef}
        className="latex-markdown-content"
        style={{ ...wrapperStyle, ...(latexInline && { display: "inline" }) }}
      >
        <span
          ref={containerRef}
          className="loading-chat-answer" /* ai-markdown-content */
          dangerouslySetInnerHTML={contentNode}
        />
      </Container>

      {/* Image popup modal (keep as is) */}
      {isImagePopupOpen && (
        <Modal
          open
          onClose={closeImagePopup}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            outline: "none",
          }}
        >
          <div
            onClick={closeImagePopup}
            style={{
              width: "70%",
              height: "70%",
              outline: "none",
              cursor: "pointer",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src={popupImageSrc}
              alt="Full Preview"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                borderRadius: "8px",
              }}
            />
          </div>
        </Modal>
      )}

      {openFile ? <FilePreviewModal2 isOpen={openFile ? true : false} file={openFile} onClose={closeFilePopup} /> : <></>}
    </>
  );
};

export default PreprocessAndRenderLatex;